/**
 * Friendly display names + icons for CMS component types — the SINGLE source of
 * truth for how a `cms.*` type is labelled in the editor UI.
 *
 * Why this exists: the name/icon maps were duplicated across AdminComponentWrapper.vue
 * and PageEditor.vue and had drifted — both were missing ~11 of the 21 registered
 * types (banner-v4, layout-grid, quote, pricing, feature-rows, …), so newer components
 * fell through to the raw registration string and the editor showed "cms.banner-v4"
 * instead of "Banner". Keeping ONE map here means a new component is named in one place.
 *
 * The keys mirror the registrations in
 * shared-components/src/mappings/componentMappings.ts. When you add a component there,
 * add its label here too.
 */

const NAME_MAP: Record<string, string> = {
  // Current standard
  'cms.banner-v4': 'Banner',
  'cms.accordion-v3': 'Accordion',
  'cms.text-section': 'Text Section',
  'cms.card-section': 'Card',
  'cms.layout-grid': 'Grid Layout',
  'cms.contact-form': 'Contact Form',
  'cms.image-gallery': 'Image Gallery',
  'cms.gallery-nav': 'Gallery Filters',
  'cms.announcement-banner': 'Announcement',
  'cms.thank-you-section': 'Thank You',
  'cms.quote': 'Quote',
  'cms.pricing': 'Pricing',
  'cms.feature-rows': 'Feature Rows',
  'cms.menu-list': 'Menu List',
  'cms.process-steps': 'Process Steps',
  'cms.location-block': 'Location',
  'cms.logo-strip': 'Logo Strip',
  // Legacy (still rendered on older pages)
  'cms.banner-section': 'Banner Section',
  'cms.banner-section-v2': 'Banner Section',
  'cms.accordion-section': 'Accordion',
  'cms.accordion-section-v2': 'Accordion',
  'cms.flex-section': 'Flex Section',
}

const ICON_MAP: Record<string, string> = {
  'cms.banner-v4': 'pi pi-image',
  'cms.accordion-v3': 'pi pi-list',
  'cms.text-section': 'pi pi-align-left',
  'cms.card-section': 'pi pi-th-large',
  'cms.layout-grid': 'pi pi-objects-column',
  'cms.contact-form': 'pi pi-envelope',
  'cms.image-gallery': 'pi pi-images',
  'cms.gallery-nav': 'pi pi-filter',
  'cms.announcement-banner': 'pi pi-megaphone',
  'cms.thank-you-section': 'pi pi-check-circle',
  'cms.quote': 'pi pi-comment',
  'cms.pricing': 'pi pi-tag',
  'cms.feature-rows': 'pi pi-bars',
  'cms.menu-list': 'pi pi-list',
  'cms.process-steps': 'pi pi-sitemap',
  'cms.location-block': 'pi pi-map-marker',
  'cms.logo-strip': 'pi pi-images',
  'cms.banner-section': 'pi pi-image',
  'cms.banner-section-v2': 'pi pi-image',
  'cms.accordion-section': 'pi pi-list',
  'cms.accordion-section-v2': 'pi pi-list',
  'cms.flex-section': 'pi pi-objects-column',
}

/** Friendly label for a component type (falls back to the raw type string). */
export function getComponentDisplayName(type: string): string {
  return NAME_MAP[type] || type
}

/** PrimeIcons class for a component type (falls back to a generic box). */
export function getComponentIcon(type: string): string {
  return ICON_MAP[type] || 'pi pi-box'
}
